import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as DateFns from 'date-fns';
import { Button, Dropdown, Tab, Tabs } from 'react-bootstrap';

import {
  CURRENT_YEAR_LABEL,
  CUSTOM_LABEL,
  DATES_LABEL,
  DEFAULT_WEEK_COMPARE_TO_DATA,
  DONE,
  FILTER_GLOBAL_KEYS,
  FILTER_KEYS,
  LIVE_PRACTICE,
  LOADING,
  MESSAGES,
  PAST_PRACTICE,
  PRACTICES_LABEL,
  SCOPE,
  USE_IN_PROGRESS_COMPS_DAYS
} from '../../../../config/constants';
import {
  getAllDateRanges,
  getFiltersDateCompareLabel,
  isNCAA,
  routeIsPractice,
  routeToScope,
  text_truncate,
  YYYYMMDDToDate
} from '../../../../helpers';
import Default from './Default';
import SeasonWeekSelector from './SeasonWeekSelector';
import SmallCalendar from './SmallCalendar';
import YearSelector from './YearSelector';
import ThresholdSelector from './ThresholdSelector';
import Table, { RowsType } from '../../CustomTable';
import Loader from '../../Loader';
import { DateRangeSelectorDataType, DateRangeSelectorPropsType, selectedDataNull } from './types';
import {
  useDataEventsState,
  useFiltersDispatch,
  useFiltersState,
  useTablePropertiesDispatch,
  useUserState
} from '../../../../store/hooks';
import { Week } from '../../../../store/types';
import { useInterval, useRouter } from '../../../../helpers/customHooks';

import './styles.scss';

export const DateRangeSelector = ({
                                    show,
                                    close,
                                    open,
                                    loadData,
                                    onlyOne,
                                    initialDate,
                                    practicesTab,
                                    forceScope,
                                    isNcaa
                                  }: DateRangeSelectorPropsType) => {

  const { pathname, specificScope: specificScopePrime } = useRouter();
  const { meta } = useDataEventsState();
  const user = useUserState();
  const specificScope = forceScope || specificScopePrime;
  const {
    [FILTER_GLOBAL_KEYS.WEEKS]: weeks = {},
    [FILTER_KEYS.DATE_RANGE]: dateRangeData,
    [FILTER_GLOBAL_KEYS.PRACTICES_DATE_RANGE_SELECTOR]: practicesDateRangeSelector
  } = useFiltersState(specificScope, FILTER_KEYS.DATE_RANGE, FILTER_KEYS.PRACTICES_DATE_RANGE_SELECTOR, FILTER_GLOBAL_KEYS.WEEKS);
  const { changeSelectedRows } = useTablePropertiesDispatch();
  const { changeFilterData, createFilterData, loadPracticesForDateRangeSelector } = useFiltersDispatch();
  const isMobile = window.matchMedia("only screen and (max-width: 900px)").matches;
  const currentDate = specificScope === SCOPE.DATAOPS.PAGE ?
  initialDate ? new Date(initialDate) : new Date()
  : DateFns.subDays(initialDate ? new Date(initialDate) : new Date(), 1);
  const currentDefaultFilter: DateRangeSelectorDataType<Week> = {
    selectedDates: [{ start: DateFns.subDays(currentDate, 6), end: currentDate }],
    default: 7,
    year: 'Select',
    week: DEFAULT_WEEK_COMPARE_TO_DATA,
    isUseInProgressComps: false,
    isUseSameIntensity: false,
    includeGamesInComparisonAvg: false,
    selectedIdPractices: [],
    compareTo: 'dates',
    threshold: 'Select'
  };
  const isRouteIsPractice = routeIsPractice(pathname);
  const isLivePractice = isRouteIsPractice && meta && meta.type === LIVE_PRACTICE;
  const isPastPractice = isRouteIsPractice && meta && meta.type === PAST_PRACTICE;
  // const selectedData: DateRangeSelectorDataType<Week> = {
  //   ...selectedDataNull,
  //   selectedDates: (dateRangeData && dateRangeData.selectedDates && dateRangeData.selectedDates || currentDefaultFilter.selectedDates)
  //     .map(date => {
  //       return { start: new Date(date.start), end: DateFns.subDays(new Date(date.end), 0) };
  //     }),
  //   ...dateRangeData
  // } as DateRangeSelectorDataType<Week>;
  const resetNewData = (dateRangeData : DateRangeSelectorDataType<Week>): DateRangeSelectorDataType<Week> => ({
    selectedDates: (dateRangeData?.selectedDates ? dateRangeData?.selectedDates : currentDefaultFilter.selectedDates)
      .map(date => {
        return { start: new Date(date.start), end: DateFns.subDays(new Date(date.end), 0) };
      }),
    default: dateRangeData?.default ? dateRangeData?.default : currentDefaultFilter.default,
    year: dateRangeData?.year ? dateRangeData?.year : currentDefaultFilter.year,
    week: dateRangeData?.week ? dateRangeData?.week : currentDefaultFilter.week,
    isUseInProgressComps: dateRangeData?.isUseInProgressComps ? dateRangeData?.isUseInProgressComps : currentDefaultFilter.isUseInProgressComps,
    isUseSameIntensity: dateRangeData?.isUseSameIntensity ? dateRangeData?.isUseSameIntensity : currentDefaultFilter.isUseSameIntensity,
    selectedIdPractices: dateRangeData?.selectedIdPractices ? dateRangeData?.selectedIdPractices : currentDefaultFilter.selectedIdPractices,
    includeGamesInComparisonAvg: dateRangeData?.includeGamesInComparisonAvg ? dateRangeData?.includeGamesInComparisonAvg : currentDefaultFilter.includeGamesInComparisonAvg,
    compareTo: dateRangeData?.compareTo ? dateRangeData?.compareTo : currentDefaultFilter.compareTo,
    threshold: isLivePractice ?  dateRangeData?.threshold ? dateRangeData?.threshold : currentDefaultFilter.threshold : currentDefaultFilter.threshold
  } as DateRangeSelectorDataType<Week>);
  const selectedData: DateRangeSelectorDataType<Week> = resetNewData(dateRangeData as DateRangeSelectorDataType<Week>);
  const scope = routeToScope(pathname);
  if (specificScope === SCOPE.PLAYERS.PAGE || specificScope === SCOPE.PRACTICES.PAGE || /*scope === SCOPE.PLAYER.PAGE ||*/
    scope === SCOPE.TEAM.TEAM_PERFORMANCE.PRACTICES.PAGE || scope === SCOPE.TEAM.TEAM_PERFORMANCE.GAMES.PAGE ||
    scope === SCOPE.TEAM.PLAYER_PERFORMANCE.PRACTICES.PAGE || scope === SCOPE.TEAM.PLAYER_PERFORMANCE.GAMES.PAGE) {
    currentDefaultFilter.selectedDates = [{ start: DateFns.subYears(currentDate, 1), end: currentDate }];
    currentDefaultFilter.default = CURRENT_YEAR_LABEL;
  }
  if (specificScope === SCOPE.GAMES.PAGE) { // games set default dates
    currentDefaultFilter.selectedDates = [{
      start: DateFns.subYears(DateFns.addWeeks(currentDate, 2), 1),
      end: DateFns.addWeeks(currentDate, 2)
    }];
    currentDefaultFilter.default = CURRENT_YEAR_LABEL;
  }


  const [newData, setNewData] = useState<DateRangeSelectorDataType<Week>>(resetNewData(dateRangeData as DateRangeSelectorDataType<Week>));
  const [appliqued, setAppliqued] = useState(false);
  const currentDateShown = selectedData.selectedDates && selectedData.selectedDates.length ? (selectedData.selectedDates[0].start) : new Date();
  const [monthShown, setMonthShown] = useState(currentDateShown);
  const [monthShown2, setMonthShown2] = useState(DateFns.addMonths(currentDateShown, 1));
  const [loadingStatus, setLoadingStatus]= useState(LOADING);

  useEffect(() => {
    createFilterData(newData, FILTER_KEYS.DATE_RANGE, forceScope)();
    if (practicesTab && practicesTab.yearSeason && practicesTab.teamId && !isMobile && Object.keys(weeks).length !== 0) {
      const dateRanges = getAllDateRanges(practicesTab.yearSeason, weeks, isNcaa ? isNcaa : isNCAA(user), true)
        .map(range => DateFns.format(YYYYMMDDToDate(range.start), 'yyyyMMdd') + '-' + DateFns.format(YYYYMMDDToDate(range.end), 'yyyyMMdd'));
      loadPracticesForDateRangeSelector(practicesTab.yearSeason, practicesTab.teamId, dateRanges)();
    }
  }, [weeks]);

  useInterval(() => {
    if(practicesDateRangeSelector && practicesTab && practicesTab.teamId && practicesTab.yearSeason &&
      practicesDateRangeSelector[practicesTab.teamId] && practicesDateRangeSelector[practicesTab.teamId][practicesTab.yearSeason]){
        if(practicesDateRangeSelector[practicesTab.teamId][practicesTab.yearSeason].status === DONE){
          setLoadingStatus(DONE);
        }
      }
    
  }, loadingStatus === LOADING? 5000: null);

  const changeDateSelected = (newData: DateRangeSelectorDataType<Week>) => changeFilterData(newData, FILTER_KEYS.DATE_RANGE, forceScope)();
  const changeSelectedTableRows = (selectedRows: string[], forceTableScope: string) => changeSelectedRows(selectedRows, false, forceTableScope)();

  const changeViewMonths = (date: Date) => {
    setMonthShown(date);
    setMonthShown2(DateFns.addMonths(date, 1));
  };
  const prevMonths = () => changeViewMonths(DateFns.subMonths(monthShown, 1));
  const nextMonths = () => changeViewMonths(DateFns.addMonths(monthShown, 1));
  const [isDoubleClicked, setIsDoubleClicked] = useState(false);
  const timerRefClick = useRef<null | ReturnType<typeof setTimeout>>(null);
  const clickSmallCalendar = useCallback(date => {
    const handlerClick = (isDoubleClick: boolean) => {
      if (newData.selectedDates.some((newDate) => (DateFns.isWithinInterval(date, {
        start: newDate.start,
        end: newDate.end
      })))) {
        setNewData({
          ...newData,
          selectedDates: newData.selectedDates.filter((newDate) => (!DateFns.isWithinInterval(date, {
            start: newDate.start,
            end: newDate.end
          })))
        });
        setIsDoubleClicked(false);
        return;
      }
      let copyNewSelectedDates = [...newData.selectedDates];
      if (isDoubleClick) {
        if (isDoubleClicked) {
          copyNewSelectedDates.pop();
          copyNewSelectedDates.push({ start: date, end: date });
        } else {
          copyNewSelectedDates.push({ start: date, end: date });
        }
      } else {
        if (isDoubleClicked) {
          const lastSelectedDate = copyNewSelectedDates[copyNewSelectedDates.length - 1];
          if (DateFns.isAfter(lastSelectedDate.start, date)) {
            copyNewSelectedDates[copyNewSelectedDates.length - 1].start = date;
          } else {
            copyNewSelectedDates[copyNewSelectedDates.length - 1].end = date;
          }
          copyNewSelectedDates = copyNewSelectedDates.filter((newDate, index) => (!(
            DateFns.isWithinInterval(newDate.start, { start: lastSelectedDate.start, end: lastSelectedDate.end }) &&
            DateFns.isWithinInterval(newDate.end, {
              start: lastSelectedDate.start,
              end: lastSelectedDate.end
            })) || index === copyNewSelectedDates.length - 1));
        } else {
          copyNewSelectedDates.push({ start: date, end: date });
        }
      }
      setIsDoubleClicked(isDoubleClick);
      setNewData({
        ...newData,
        selectedDates: onlyOne ? copyNewSelectedDates.length > 1 ? [copyNewSelectedDates[1]] : [...copyNewSelectedDates] : [...copyNewSelectedDates],
        week: DEFAULT_WEEK_COMPARE_TO_DATA,
        default: CUSTOM_LABEL
      });
    };
    const timerIsPresent = !!timerRefClick.current; //if timer exist so a click happened < 200 ms
    if (timerIsPresent) {
      if (timerRefClick.current) {
        clearTimeout(timerRefClick.current);
      }
      timerRefClick.current = null;
      handlerClick(true);
    }
    if (!timerIsPresent) {
      timerRefClick.current = setTimeout(() => {
        if (timerRefClick.current) {
          clearTimeout(timerRefClick.current);
        }
        timerRefClick.current = null;
        handlerClick(false);
      }, 200);
    }
  }, [newData, onlyOne, isDoubleClicked]);
  const forceTableScope = `DATE_RANGE_SELECTOR_PRACTICES:${practicesTab && practicesTab.teamId}`;
  const onClose = () => {
    close();
    const defaultResetNewData = resetNewData(dateRangeData as DateRangeSelectorDataType<Week>);
    setNewData(defaultResetNewData);
    changeSelectedTableRows(defaultResetNewData.selectedIdPractices, forceTableScope);
    setIsDoubleClicked(false);
    changeViewMonths(currentDateShown);
  };

  const onOpen = () => {
    open();
    const defaultResetNewData = resetNewData(dateRangeData as DateRangeSelectorDataType<Week>);
    setNewData(defaultResetNewData);
    changeSelectedTableRows(defaultResetNewData.selectedIdPractices, forceTableScope);
    changeViewMonths(currentDateShown);
  };
  const headers = [
    { class: 'oo-i l-ll t-ll', style: { width: '52px' }, isCheckboxCell: true, isSelectable: true }, //isCheckboxCell: put in content a checkbox, isSelectable: select all or nothing
    { content: 'NAME', style: { width: '266px', paddingLeft: '20px' }, isOrderly: true, leftSortIcon: '27px' },
    { content: 'DATE/TIME', style: { width: '180px' }, isOrderly: true, leftSortIcon: '27px' },
    { content: 'INTENSITY', style: { width: '150px' }, isOrderly: true, class: 't-ll' }
  ];
  const rows: RowsType<string, number> = [];
   if (practicesTab && practicesTab.teamId && practicesTab.yearSeason && practicesDateRangeSelector && practicesDateRangeSelector[practicesTab.teamId] && 
    practicesDateRangeSelector[practicesTab.teamId][practicesTab.yearSeason]) {
    practicesDateRangeSelector[practicesTab.teamId][practicesTab.yearSeason].practices.forEach((practice: any) => {
      rows.push({
        columns: [
          { class: 'oo-i l-ll t-ll', isCheckboxCell: true },
          {
            content: practice.name,
            print: (content: string) => (
              <label title={content} className="l-c" style={{ cursor: 'pointer' }}>
                <b>{content}</b>
              </label>
            ),
            order: (contentA: string, contentB: string) => contentA.localeCompare(contentB),
            style: { cursor: 'pointer', paddingLeft: '20px' }
          },
          {
            content: practice.startTime,// style: {width: '12%'}
            print: (content: number) => (
              <label className="l-c">{DateFns.format(new Date(content), 'MM/dd/yyyy, h:mma')}</label>
            ),
            order: (contentA: number, contentB: number) => contentA - contentB
          },
          {
            content: practice.intensity, //style: {width: '6%'}
            print: (content: string) => <label className="l-c">{content}</label>,
            order: (contentA: string, contentB: string) => contentA.localeCompare(contentB),
            class: 't-ll'
          }
        ],
        id: practice.gameId
      });
    });
  }

  const shouldDisableApplyBtn = () => {
    return +newData.selectedDates.length === 0 ||
      (newData.compareTo === PRACTICES_LABEL && !newData.selectedIdPractices.length);
  };

  return (
    <Dropdown className="dropdown-date-compare filter-ff" show={show} onToggle={show ? onClose : onOpen}>
      <Dropdown.Toggle
        id="dropdown-date-compare"
        className="btn-border gameff"
        style={(specificScope === SCOPE.DATAOPS.PAGE ? { width: '185px' } : { minWidth: '172px' })}
      >
        <label className={appliqued ? 'appliqued' : ''}>
          {specificScope === SCOPE.PRACTICES.PAGE || specificScope === SCOPE.GAMES.PAGE || specificScope === SCOPE.DATAOPS.PAGE
          /*|| scope === SCOPE.PLAYER.PAGE*/ || scope === SCOPE.PLAYERS.PAGE || scope === SCOPE.TEAM.TEAM_PERFORMANCE.PRACTICES.PAGE || scope === SCOPE.TEAM.TEAM_PERFORMANCE.GAMES.PAGE ||
          scope === SCOPE.TEAM.PLAYER_PERFORMANCE.PRACTICES.PAGE || scope === SCOPE.TEAM.PLAYER_PERFORMANCE.GAMES.PAGE ? 'DATE' : 'COMPARE TO'}</label>
        {isMobile? text_truncate(getFiltersDateCompareLabel(selectedData, specificScope).toString(),10) : getFiltersDateCompareLabel(selectedData, specificScope)}
      </Dropdown.Toggle>
      <Dropdown.Menu className='no-offset'>
        <div
          className={'calendar-01' + (!practicesTab ? ' only-tab-dates' : '') + (practicesDateRangeSelector ? ' live-practice' : '')}>
          <Tabs
            id="uncontrolled-tab-date-rage-selector"
            onSelect={(eventKey: string | null) => setNewData({
              ...newData,
              compareTo: eventKey === DATES_LABEL ? DATES_LABEL : PRACTICES_LABEL
            })}
          >
            <Tab eventKey={DATES_LABEL} title="Dates">
              <Default
                newData={newData}
                setNewData={(data: DateRangeSelectorDataType<Week>) => {
                  setNewData(data);
                  setIsDoubleClicked(false);
                }}
                changeViewMonths={changeViewMonths}
                now={initialDate ? initialDate : new Date()}
                specificScope={specificScope}
              />
              <div className="calendar-h-01">
                <input
                  className={`styled-checkbox`}
                  id="styled-checkbox-13"
                  type="checkbox"
                  value="value13"
                  checked={newData.default === CUSTOM_LABEL}
                  onChange={() => {
                    setNewData({
                      ...newData,
                      default: CUSTOM_LABEL,
                      selectedDates: []
                    });
                  }}
                />
                <label
                  htmlFor="styled-checkbox-13"
                  className={`check-l ${newData.default === CUSTOM_LABEL ? 'label-bold' : ''}`}
                >
                  Custom
                </label>
                {isDoubleClicked && <label className="label-info-help">Select date range</label>}
                <div className="select-r">
                  <YearSelector
                    newData={newData}
                    setNewData={setNewData}
                    changeViewMonths={changeViewMonths}
                  />
                  {!isNCAA(user) ? (
                    <SeasonWeekSelector
                      newData={newData}
                      setNewData={(data: DateRangeSelectorDataType<Week>) => {
                        setNewData(data);
                        setIsDoubleClicked(false);
                      }}
                      changeViewMonths={changeViewMonths}
                      weeksData={(weeks && weeks[newData.year]) || []}
                    />
                  ) : null}
                </div>
              </div>
              <div className="calendar-body">
                <Button className="btn-04" onClick={prevMonths}>
                  <i className="mdi mdi-arrow-left" />
                </Button>
                <SmallCalendar
                  monthShown={monthShown}
                  newSelectedDates={newData.selectedDates}
                  click={clickSmallCalendar}
                  newData={newData}
                  currentDate={currentDate}
                />
                <SmallCalendar
                  monthShown={monthShown2}
                  newSelectedDates={newData.selectedDates}
                  click={clickSmallCalendar}
                  newData={newData}
                  currentDate={currentDate}
                />
                <Button className="btn-05" onClick={nextMonths}><i className="mdi mdi-arrow-right" /></Button>
              </div>
              {(isLivePractice || isPastPractice) && (
                <>
                <div className="calendar-other-options">
                  <div>Other Options:</div>
                  <div className="content-calendar-other-options">
                    {isLivePractice && (
                      <div className="user-in-progress-comps-box">
                        <input
                          type="checkbox"
                          id="user-in-progress-comps-input"
                          name="set-name"
                          className="switch-input"
                          checked={newData.isUseInProgressComps}
                          onChange={() => {
                            setNewData({
                              ...newData,
                              default: newData.isUseInProgressComps ? newData.default : CUSTOM_LABEL,
                              selectedDates: newData.selectedDates.filter((range) => (
                                DateFns.isWithinInterval(range.start, {
                                  start: DateFns.subDays(currentDate, USE_IN_PROGRESS_COMPS_DAYS),
                                  end: currentDate
                                }) && DateFns.isWithinInterval(range.end, {
                                  start: DateFns.subDays(currentDate, USE_IN_PROGRESS_COMPS_DAYS),
                                  end: currentDate
                                }))),
                              isUseInProgressComps: !newData.isUseInProgressComps
                            });
                            changeViewMonths(DateFns.subDays(currentDate, USE_IN_PROGRESS_COMPS_DAYS));
                          }}
                        />
                        <label htmlFor="user-in-progress-comps-input" className="switch-label" />
                        <label className={'title' + (newData.isUseInProgressComps ? ' title-selected' : '')}>
                          Use In-Progress Comps
                        </label>
                      </div>
                    )}
                    <div className="user-same-intensity-practices-only-box">
                      <input
                        type="checkbox"
                        id="use-same-intensity-practices-only-input"
                        name="set-name"
                        className="switch-input"
                        checked={newData.isUseSameIntensity}
                        onChange={() => {
                          setNewData({
                            ...newData,
                            isUseSameIntensity: !newData.isUseSameIntensity
                          });
                        }}
                      />
                      <label htmlFor="use-same-intensity-practices-only-input" className="switch-label" />
                      <label className={'title' + (newData.isUseSameIntensity ? ' title-selected' : '')}>
                        Use Same Intensity Practices Only
                      </label>
                    </div>
                    <div className="include-games-in-comparison-average">
                        <input
                          type="checkbox"
                          id="include-games-in-comparison-average"
                          name="set-name"
                          className="switch-input"
                          checked={newData.includeGamesInComparisonAvg}
                          onChange={() => {
                            setNewData({
                              ...newData,
                              includeGamesInComparisonAvg: !newData.includeGamesInComparisonAvg
                            });
                        }}
                        />
                        <label htmlFor="include-games-in-comparison-average" className="switch-label" />
                        <label className={'title' + (newData.includeGamesInComparisonAvg ? ' title-selected' : '')}>
                          Include Games in Comparison Average
                        </label>
                    </div>
                  </div>
                  { isLivePractice && (
                    <div className='select-r thersholdFilter'>
                    <ThresholdSelector
                    newData={newData}
                    setNewData={setNewData}
                    />
                  </div>
                  )}
                </div>
                </>
              )}
            </Tab>
            <Tab eventKey={PRACTICES_LABEL} title="Practices">
              <div className="table-date-range-selector-content">
                <Loader
                  forceLoader={loadingStatus === LOADING}
                  forceMessage={(!practicesDateRangeSelector || !practicesTab || !practicesTab.teamId || !practicesTab.yearSeason ||
                    !practicesDateRangeSelector[practicesTab.teamId] || !practicesDateRangeSelector[practicesTab.teamId][practicesTab.yearSeason] ||
                    (practicesDateRangeSelector[practicesTab.teamId][practicesTab.yearSeason].status === DONE &&
                      practicesDateRangeSelector[practicesTab.teamId][practicesTab.yearSeason].practices.length === 0)) ? MESSAGES.EMPTY : undefined}>
                  <Table
                    headers={headers}
                    rows={rows}
                    default={{ orderColumn: 3 }}
                    isSelectedStyle
                    forceScope={forceTableScope}
                    onChangeSelectedRows={(selectedRows: string[]) => {
                      setNewData({
                        ...newData,
                        selectedIdPractices: selectedRows
                      });
                    }}
                    classTable="table-with-borders"
                  />
                </Loader>
              </div>
            </Tab>
          </Tabs>
          <div className="footer-s">
            <button className="btn-transparet" onClick={onClose}>Cancel</button>
            <button
              disabled={shouldDisableApplyBtn()}
              className={'btn-blue ' + (shouldDisableApplyBtn() ? 'opacity-50' : '')}
              onClick={() => {
                close();
                setAppliqued(true);
                changeDateSelected(newData);
                loadData();
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export * from './types';
